// frontend/src/components/TimelineDrawer.jsx
import React, { useEffect, useState } from 'react';
import { getAuditLog } from '../api';

// Human labels for the audit actions the backend emits.
const ACTION_META = {
  create:        { label: 'Created',          icon: '✨', color: '#059669' },
  update:        { label: 'Changed',          icon: '✏️', color: '#2563eb' },
  status_change: { label: 'Status changed',   icon: '🔀', color: '#7c3aed' },
  assignment:    { label: 'Assignment',       icon: '👥', color: '#d97706' },
  delete:        { label: 'Deleted',          icon: '🗑️', color: '#dc2626' },
  archive:       { label: 'Archived',         icon: '📦', color: '#6b7280' },
  restore:       { label: 'Restored',         icon: '♻️', color: '#059669' },
};

// Prettify a snake_case field name for display.
function fieldLabel(field) {
  if (!field) return '';
  return field
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function describe(entry) {
  if (entry.action === 'create') return 'Record created';
  if (entry.action === 'delete') return 'Record deleted';
  if (entry.action === 'archive') return 'Archived';
  if (entry.action === 'restore') return 'Restored from archive';
  if (entry.action === 'assignment') return 'Assigned employees changed';
  const f = fieldLabel(entry.field);
  const from = entry.old_value || '—';
  const to = entry.new_value || '—';
  return `${f}: ${from} → ${to}`;
}

function formatWhen(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/**
 * Sliding right-hand drawer that shows one record's audit history as a
 * timeline, newest first.
 *
 * Props:
 *   open        boolean
 *   onClose     () => void
 *   recordType  'task' | 'client' | 'invoice' | 'timeentry'
 *   recordId    number | string
 *   title       optional heading (defaults to "History")
 */
export default function TimelineDrawer({ open, onClose, recordType, recordId, title }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !recordType || !recordId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getAuditLog(recordType, recordId)
      .then((data) => {
        if (!cancelled) setEntries(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load history.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, recordType, recordId]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
          zIndex: 1000,
        }}
      />
      {/* Drawer */}
      <div
        role="dialog"
        aria-label="History timeline"
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: 420,
          maxWidth: '90vw', background: '#fff', zIndex: 1001,
          boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid #e5e7eb',
        }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{title || 'History'}</div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent', border: 'none', fontSize: 22,
              cursor: 'pointer', color: '#6b7280', lineHeight: 1,
            }}
            aria-label="Close history"
          >×</button>
        </div>

        <div style={{ overflowY: 'auto', padding: 20, flex: 1 }}>
          {loading && <div style={{ color: '#6b7280' }}>Loading…</div>}
          {error && <div style={{ color: '#dc2626' }}>{error}</div>}
          {!loading && !error && entries.length === 0 && (
            <div style={{ color: '#6b7280' }}>No history recorded yet.</div>
          )}

          {!loading && !error && entries.length > 0 && (
            <div style={{ position: 'relative', paddingLeft: 8 }}>
              {entries.map((e) => {
                const meta = ACTION_META[e.action] || { icon: '•', color: '#6b7280' };
                return (
                  <div key={e.id} style={{ display: 'flex', gap: 12, paddingBottom: 20 }}>
                    <div style={{
                      flex: '0 0 auto', width: 28, height: 28, borderRadius: '50%',
                      background: '#f3f4f6', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontSize: 14,
                    }}>{meta.icon}</div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 14, color: '#111827', wordBreak: 'break-word' }}>
                        {describe(e)}
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                        {e.actor_name || 'System'} · {formatWhen(e.created_at)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
